import React from 'react';
import { MinusIcon, PlusIcon } from 'lucide-react';
export interface GuaranteeItem {
  /** Nombre del icono de la biblioteca (Zap, ShieldCheck, Lock…). */
  icon?: string;
  title: string;
  text: string;
  /** Con «no» el acordeón no se muestra en la landing pública. */
  visible?: string;
}
interface GuaranteeAccordionProps {
  items: GuaranteeItem[];
  /** 'uno' = solo uno abierto a la vez; 'varios' = varios simultáneos. */
  mode?: 'uno' | 'varios';
  /** Abre el primer acordeón al cargar. Por defecto los tres cerrados. */
  firstOpen?: boolean;
  /** Resuelve el icono configurado desde el constructor. */
  renderIcon: (name: string | undefined, className: string) => React.ReactNode;
}

/**
 * Acordeones de garantías: cerrados por defecto, toda la fila es pulsable y el
 * contenido se despliega con una transición suave (sin recargar la página ni
 * desplazar la maquetación).
 * Mobile first: una tarjeta por fila, icono a la izquierda, título en el centro,
 * símbolo + / − a la derecha y área táctil de 44 px como mínimo.
 */
export function GuaranteeAccordion({
  items,
  mode = 'uno',
  firstOpen = false,
  renderIcon
}: GuaranteeAccordionProps) {
  const [open, setOpen] = React.useState<number[]>(firstOpen ? [0] : []);
  function toggle(index: number) {
    setOpen(current => {
      if (current.includes(index)) return current.filter(i => i !== index);
      return mode === 'varios' ? [...current, index] : [index];
    });
  }
  if (items.length === 0) return null;
  return <div className="space-y-3 sm:space-y-4" data-id="element-604" data-mp="3he3sswjpkm0m">
      {items.map((item, index) => {
      const isOpen = open.includes(index);
      const panelId = `garantia-${index}`;
      return <div key={`${item.title}-${index}`} className={`overflow-hidden rounded-2xl border bg-background transition-colors duration-200 ${isOpen ? 'border-primary/40' : 'border-border'}`} data-id="element-605" data-mp="3rd1medvzr5qh" data-mpi={index}>
            <button type="button" aria-expanded={isOpen} aria-controls={panelId} onClick={() => toggle(index)} className="flex min-h-[64px] w-full items-center gap-3 px-4 py-4 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 sm:min-h-[72px] sm:gap-4 sm:px-6 sm:py-5" data-id="element-606" data-mp="16mu68ptrqfj0">
              <span className="flex h-11 w-11 flex-none items-center justify-center rounded-full bg-primary/10" data-id="element-607" data-mp="qwur25hsfx7z">
                {renderIcon(item.icon, 'h-5 w-5 text-primary')}
              </span>
              <span className="min-w-0 flex-1 break-words text-[18px] font-semibold leading-[1.25] text-navy sm:text-[19px]" data-id="element-608" data-mp="3udl1qxobeyg0">
                {item.title}
              </span>
              <span className={`flex h-8 w-8 flex-none items-center justify-center rounded-full transition-colors duration-200 ${isOpen ? 'bg-primary text-white' : 'bg-surface text-primary'}`} aria-hidden="true" data-id="element-609" data-mp="3pgbdhlcnwstr">
                {isOpen ? <MinusIcon className="h-4 w-4" data-id="element-610" data-mp="3bn3p02jvodga" /> : <PlusIcon className="h-4 w-4" data-id="element-611" data-mp="3bmahgzd3hjx3" />}
              </span>
            </button>
            <div id={panelId} aria-hidden={!isOpen} className={`grid transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none ${isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`} data-id="element-612" data-mp="36oikhqxlf7m1">
              <div className="overflow-hidden" data-id="element-613" data-mp="1qwy7hdsclog3">
                <p className="px-4 pb-4 text-[15px] leading-[1.55] text-muted-foreground sm:px-6 sm:pb-6 sm:pl-[74px] sm:text-[15px] sm:leading-relaxed" data-id="element-614" data-mp="1qyrif59zuutw">
                  {item.text}
                </p>
              </div>
            </div>
          </div>;
    })}
    </div>;
}